/**
 * A Rich Text Format reader (§8, RTF 1.9.1).
 *
 * RTF is not a container and not XML: it is a stream of `{groups}`, `\controls`
 * and literal text, where formatting is state that the braces push and pop. So
 * this is a state-machine scanner, not a tree build — it walks the bytes once,
 * carrying a stack of character and paragraph state, and emits paragraphs of
 * runs shaped like the ones the DOCX reader produces. That shape is what lets an
 * RTF open in the same document viewer as everything else (§16).
 *
 * Three things about RTF decide the structure here, and each is a place a naive
 * reader goes silently wrong:
 *
 * - **Most groups are not body text.** The font table, colour table, style
 *   sheet, document info and every `{\*\…}` "ignorable destination" carry
 *   metadata, not content. A reader that emits their text prints the font
 *   names and the RSID table into the document. So a destination that is not
 *   body text is skipped whole.
 * - **Non-ASCII arrives two different ways.** `\uN` is a Unicode code point
 *   followed by `\ucN` fallback bytes that must be *dropped*, and `\'hh` is a
 *   raw byte in the document's code page, which for a Korean file is a DBCS
 *   lead/trail pair that only means anything decoded together. Getting either
 *   wrong turns Hangul into mojibake.
 * - **`\objdata` can hide an executable.** §30 and the catalog both flag it:
 *   an embedded OLE object is an attack surface, so its data is never decoded
 *   and its presence is reported instead.
 */
import type { DocxBlock, DocxParagraph, DocxRun } from '../docx/document-xml.js';

export interface RtfDocument {
  readonly blocks: readonly DocxBlock[];
  readonly paragraphs: readonly DocxParagraph[];
  /** Code page declared by `\ansicpg`, for the information screen. */
  readonly codePage: number;
  /** True when the file embeds an OLE object (`\object`/`\objdata`) (§30). */
  readonly hasEmbeddedObjects: boolean;
  /** True when a picture group was present but not rendered. */
  readonly hasPictures: boolean;
  readonly wordCount: number;
  readonly truncated: boolean;
}

interface CharState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  vertical: 'super' | 'sub' | null;
}

interface ParaState {
  alignment: 'left' | 'center' | 'right' | 'justify' | null;
}

interface Frame {
  char: CharState;
  para: ParaState;
  /** Unicode fallback bytes to skip after a `\uN`, from `\ucN`. */
  uc: number;
  /**
   * When set, everything in this group is discarded rather than emitted — a
   * destination whose content is not body text. Inherited by child groups.
   */
  destination: string | null;
}

/** Groups whose entire content is metadata, never body text. */
const SKIP_DESTINATIONS = new Set([
  'fonttbl', 'colortbl', 'stylesheet', 'info', 'pict', 'object', 'objdata',
  'listtable', 'listoverridetable', 'revtbl', 'rsidtbl', 'generator',
  'themedata', 'colorschememapping', 'latentstyles', 'datastore',
  'xmlnstbl', 'fldinst', 'filetbl', 'protusertbl', 'wgrffmtfilter',
  'header', 'footer', 'headerl', 'headerr', 'footerl', 'footerr',
  'headerf', 'footerf', 'pgdsctbl', 'bkmkstart', 'bkmkend',
]);

/** Control words that stand for a single literal character. */
const CHAR_ESCAPES: Readonly<Record<string, string>> = {
  tab: '\t', line: '\n', emdash: '—', endash: '–',
  lquote: '‘', rquote: '’', ldblquote: '“', rdblquote: '”',
  bullet: '•', emspace: ' ', enspace: ' ', '~': ' ',
  '_': '‑', '-': '­',
};

/**
 * Parse an RTF document into paragraphs of runs.
 *
 * @param maxParagraphs A budget: a hostile or enormous file must not build an
 *   unbounded model (§33). Reaching it sets `truncated`.
 */
export function parseRtf(text: string, maxParagraphs = 100_000): RtfDocument {
  const initial: Frame = {
    char: { bold: false, italic: false, underline: false, strike: false, vertical: null },
    para: { alignment: null },
    uc: 1,
    destination: null,
  };
  const stack: Frame[] = [initial];
  let frame = initial;

  const paragraphs: DocxParagraph[] = [];
  let runs: DocxRun[] = [];
  let text_ = '';
  let index = 0;
  let codePage = 1252;
  let hasEmbeddedObjects = false;
  let hasPictures = false;
  let truncated = false;

  /** Bytes from `\'hh`, buffered so a DBCS pair decodes as one character. */
  let byteBuffer: number[] = [];

  const flushBytes = (): void => {
    if (byteBuffer.length === 0) return;
    const decoded = decodeBytes(Uint8Array.from(byteBuffer), codePage);
    byteBuffer = [];
    if (frame.destination === null) appendText(decoded);
  };

  function appendText(s: string): void {
    if (!s) return;
    text_ += s;
    const last = runs[runs.length - 1];
    if (last && last.kind === 'text'
      && last.bold === frame.char.bold && last.italic === frame.char.italic
      && last.underline === frame.char.underline && last.strike === frame.char.strike
      && last.vertical === frame.char.vertical) {
      runs[runs.length - 1] = { ...last, text: last.text + s };
      return;
    }
    runs.push({
      kind: 'text', text: s,
      bold: frame.char.bold, italic: frame.char.italic,
      underline: frame.char.underline, strike: frame.char.strike,
      vertical: frame.char.vertical, highlight: null, color: null,
      href: null, revision: null,
    });
  }

  function endParagraph(): void {
    if (paragraphs.length >= maxParagraphs) { truncated = true; return; }
    const flat = text_;
    paragraphs.push({
      kind: 'paragraph', index: index++,
      styleId: null, styleName: null, headingLevel: null,
      alignment: frame.para.alignment, listLevel: null, listOrdered: false,
      runs, text: flat, originalText: flat,
      // RTF editing is conversion-based (§ catalog convert-edit); nothing here
      // is offered for in-place editing, so the flag is honest at false.
      editable: false, start: 0, end: 0,
    });
    runs = [];
    text_ = '';
  }

  let i = 0;
  const n = text.length;

  while (i < n && !truncated) {
    const ch = text[i]!;

    if (ch === '{') {
      flushBytes();
      const copy: Frame = {
        char: { ...frame.char }, para: { ...frame.para },
        uc: frame.uc, destination: frame.destination,
      };
      stack.push(copy);
      frame = copy;
      i += 1;
      continue;
    }

    if (ch === '}') {
      flushBytes();
      stack.pop();
      frame = stack[stack.length - 1] ?? initial;
      i += 1;
      continue;
    }

    if (ch === '\\') {
      const next = text[i + 1];
      // Escaped literal or control symbol.
      if (next === '\\' || next === '{' || next === '}') {
        flushBytes();
        if (frame.destination === null) appendText(next!);
        i += 2;
        continue;
      }
      if (next === '*') {
        // An ignorable destination: mark the current group to be skipped unless
        // it is one whose content we do want. The word follows.
        i += 2;
        // The next control word names the destination; reading it below will
        // set the destination, but a `\*` before an unknown one means skip.
        frame.destination = frame.destination ?? '*';
        continue;
      }
      if (next === "'") {
        // \'hh — one raw byte in the document code page.
        const hex = text.slice(i + 2, i + 4);
        const byte = Number.parseInt(hex, 16);
        if (Number.isFinite(byte)) byteBuffer.push(byte);
        i += 4;
        continue;
      }

      // A control word: letters, then an optional signed integer, then an
      // optional single space that is part of the delimiter.
      const word = readControlWord(text, i + 1);
      if (!word) {
        // A lone backslash followed by a non-alpha, non-symbol byte — a control
        // symbol we do not special-case; treat as literal.
        flushBytes();
        if (frame.destination === null && next) appendText(next);
        i += 2;
        continue;
      }
      flushBytes();
      i = word.next;
      applyControl(word.name, word.param);
      continue;
    }

    // Ordinary text. CR/LF between control words are not content in RTF.
    if (ch === '\r' || ch === '\n') { i += 1; continue; }
    // Any buffered `\'hh` bytes belong before this literal, not after it.
    flushBytes();
    if (frame.destination === null) appendText(ch);
    i += 1;
  }

  flushBytes();
  // Trailing text with no closing \par is still a paragraph.
  if (text_ !== '' || runs.length > 0) endParagraph();

  const blocks: DocxBlock[] = paragraphs.slice();
  return {
    blocks, paragraphs, codePage, hasEmbeddedObjects, hasPictures,
    wordCount: countWords(paragraphs), truncated,
  };

  /* -------------------------------------------------------------- */

  function applyControl(name: string, param: number | null): void {
    // A control word that names a skip destination silences the whole group.
    if (SKIP_DESTINATIONS.has(name)) {
      if (name === 'object' || name === 'objdata') hasEmbeddedObjects = true;
      if (name === 'pict') hasPictures = true;
      frame.destination = name;
      return;
    }

    switch (name) {
      case 'ansicpg': if (param !== null) codePage = param; return;
      case 'bin':
        // `\binN` is followed by exactly N raw bytes, which may contain
        // `{`, `}` or `\` that are data, not structure. Skipping them by count
        // is the only safe read — otherwise a picture's binary reorganises the
        // group nesting and the rest of the document vanishes.
        if (param !== null && param > 0) i = Math.min(n, i + param);
        return;
      case 'par': case 'pard': if (name === 'par') { endParagraph(); return; } break;
      case 'line': appendIfBody('\n'); return;
      case 'tab': appendIfBody('\t'); return;
      case 'uc': frame.uc = param ?? 1; return;
      case 'u': {
        if (param === null) return;
        const code = param < 0 ? param + 0x10000 : param;
        appendIfBody(String.fromCodePoint(code & 0xffff));
        skipUnicodeFallback();
        return;
      }
      case 'plain':
        frame.char = { bold: false, italic: false, underline: false, strike: false, vertical: null };
        return;
      case 'b': frame.char.bold = param !== 0; return;
      case 'i': frame.char.italic = param !== 0; return;
      case 'ul': frame.char.underline = param !== 0; return;
      case 'ulnone': frame.char.underline = false; return;
      case 'strike': frame.char.strike = param !== 0; return;
      case 'super': frame.char.vertical = param === 0 ? null : 'super'; return;
      case 'sub': frame.char.vertical = param === 0 ? null : 'sub'; return;
      case 'nosupersub': frame.char.vertical = null; return;
      case 'ql': frame.para.alignment = 'left'; return;
      case 'qc': frame.para.alignment = 'center'; return;
      case 'qr': frame.para.alignment = 'right'; return;
      case 'qj': frame.para.alignment = 'justify'; return;
      default: return; // an unrecognised control word contributes nothing
    }

    // `\pard` resets paragraph formatting without ending the paragraph.
    if (name === 'pard') frame.para = { alignment: null };
  }

  function appendIfBody(s: string): void {
    if (frame.destination === null) appendText(s);
  }

  /** After a `\uN`, drop the code page fallback that follows it. */
  function skipUnicodeFallback(): void {
    let dropped = 0;
    while (dropped < frame.uc && i < n) {
      const c = text[i]!;
      if (c === '\\' && text[i + 1] === "'") { i += 4; dropped += 1; continue; }
      if (c === '{' || c === '}' || c === '\\') break;
      i += 1; dropped += 1;
    }
  }
}

/* ------------------------------------------------------------------ *
 * scanning
 * ------------------------------------------------------------------ */

interface ControlWord { name: string; param: number | null; next: number }

function readControlWord(text: string, from: number): ControlWord | null {
  let i = from;
  let name = '';
  while (i < text.length) {
    const c = text.charCodeAt(i);
    // a–z or A–Z
    if ((c >= 97 && c <= 122) || (c >= 65 && c <= 90)) { name += text[i]; i += 1; }
    else break;
  }
  if (name === '') return null;

  let param: number | null = null;
  let sign = 1;
  if (text[i] === '-') { sign = -1; i += 1; }
  let digits = '';
  while (i < text.length) {
    const c = text.charCodeAt(i);
    if (c >= 48 && c <= 57) { digits += text[i]; i += 1; } else break;
  }
  if (digits !== '') param = sign * Number.parseInt(digits, 10);

  // A single trailing space is the control word's delimiter and is consumed.
  if (text[i] === ' ') i += 1;
  return { name, param, next: i };
}

/* ------------------------------------------------------------------ *
 * bytes
 * ------------------------------------------------------------------ */

/** Label a `\ansicpg` number as an encoding a TextDecoder understands. */
function encodingLabel(codePage: number): string {
  switch (codePage) {
    case 932: return 'shift_jis';
    case 936: return 'gbk';
    case 949: return 'euc-kr';
    case 950: return 'big5';
    case 1200: return 'utf-16le';
    case 65001: return 'utf-8';
    default: return `windows-${codePage}`;
  }
}

function decodeBytes(bytes: Uint8Array, codePage: number): string {
  try {
    return new TextDecoder(encodingLabel(codePage), { fatal: false }).decode(bytes);
  } catch {
    // An unknown code page: fall back to Latin-1, which at least never throws
    // and keeps ASCII intact.
    try {
      return new TextDecoder('windows-1252', { fatal: false }).decode(bytes);
    } catch {
      return '';
    }
  }
}

function countWords(paragraphs: readonly DocxParagraph[]): number {
  let total = 0;
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.text.trim();
    if (trimmed) total += trimmed.split(/\s+/).length;
  }
  return total;
}

/** Plain text of the whole document, for search and copy. */
export function rtfToPlainText(doc: RtfDocument): string {
  return doc.paragraphs.map((p) => p.text).join('\n');
}
